import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API_URL = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8000';

/** Ab diesem Alter gilt eine Notierung als veraltet — identisch zum
 *  Warning-Engine-Check (preiskondition_service.NOTIERUNG_MAX_ALTER_TAGE). */
const MAX_ALTER_TAGE = 7;

interface Notierung {
  id: number;
  metall: string;
  metall_bezeichnung: string;
  datum: string;
  kurs_eur_100kg: number;
  quelle: string | null;
}

const heuteISO = () => new Date().toISOString().slice(0, 10);

const alterInTagen = (datum: string): number =>
  Math.floor((Date.parse(heuteISO()) - Date.parse(datum)) / 86400000);

const formatDatum = (iso: string): string => {
  const [j, m, t] = iso.split('-');
  return `${t}.${m}.${j}`;
};

/**
 * Pflege der Metall-Tagesnotierungen (AP 3.5).
 *
 * Die Kurse werden bewusst manuell erfasst: die Quelle (Westmetall DEL-Notiz)
 * hat kein freies API, ein Scraper waere ein eigenes Betriebsrisiko. Pro
 * Metall und Tag ist genau ein Kurs zulaessig — ein zweiter Eintrag fuer
 * denselben Tag wird serverseitig mit 409 abgewiesen, Korrekturen laufen als
 * Aenderung durch den Audit-Trail.
 */
const MetallSection: React.FC = () => {
  const [notierungen, setNotierungen] = useState<Notierung[]>([]);
  const [loading, setLoading] = useState(true);
  const [fehler, setFehler] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    metall: 'CU',
    datum: heuteISO(),
    kurs_eur_100kg: '',
    quelle: 'Westmetall DEL-Notiz',
  });

  const load = useCallback(() => {
    setLoading(true);
    setFehler(null);
    axios.get<Notierung[]>(`${API_URL}/api/metall-notierungen`, { params: { limit: 30 } })
      .then(r => setNotierungen(r.data))
      .catch((err: unknown) => {
        const msg = axios.isAxiosError(err) ? (err.response?.data?.detail ?? err.message) : 'Unbekannter Fehler';
        setFehler(`Notierungen konnten nicht geladen werden: ${msg}`);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setFehler(null);
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/metall-notierungen`, {
        metall: form.metall,
        datum: form.datum,
        kurs_eur_100kg: parseFloat(form.kurs_eur_100kg),
        quelle: form.quelle || null,
      });
      setForm(f => ({ ...f, kurs_eur_100kg: '' }));
      load();
    } catch (err: unknown) {
      // 409 = Kurs fuer diesen Tag existiert schon; der Server nennt im
      // detail die bestehende ID und den Kurs — woertlich anzeigen.
      const msg = axios.isAxiosError(err) ? (err.response?.data?.detail ?? err.message) : 'Unbekannter Fehler';
      setFehler(String(msg));
    } finally {
      setSaving(false);
    }
  };

  const handleKursKorrigieren = async (n: Notierung) => {
    const eingabe = window.prompt(
      `Kurs für ${n.metall_bezeichnung} vom ${formatDatum(n.datum)} korrigieren (EUR / 100 kg):`,
      String(n.kurs_eur_100kg),
    );
    if (eingabe === null) return;
    const wert = parseFloat(eingabe.replace(',', '.'));
    if (!Number.isFinite(wert) || wert <= 0) {
      setFehler('Ungültiger Kurs — bitte eine Zahl größer als 0 eingeben.');
      return;
    }
    setFehler(null);
    try {
      await axios.put(`${API_URL}/api/metall-notierungen/${n.id}`, { kurs_eur_100kg: wert });
      load();
    } catch (err: unknown) {
      const msg = axios.isAxiosError(err) ? (err.response?.data?.detail ?? err.message) : 'Unbekannter Fehler';
      setFehler(String(msg));
    }
  };

  const handleLoeschen = async (n: Notierung) => {
    const ok = window.confirm(
      `Notierung ${n.metall} vom ${formatDatum(n.datum)} löschen?\n\n` +
      'Bereits geschriebene Positionen behalten ihren eingefrorenen Zuschlag — ' +
      'nur künftige Kalkulationen sind betroffen.',
    );
    if (!ok) return;
    setFehler(null);
    try {
      await axios.delete(`${API_URL}/api/metall-notierungen/${n.id}`);
      load();
    } catch (err: unknown) {
      const msg = axios.isAxiosError(err) ? (err.response?.data?.detail ?? err.message) : 'Unbekannter Fehler';
      setFehler(String(msg));
    }
  };

  const juengsteCu = notierungen.find(n => n.metall === 'CU');
  const cuVeraltet = !juengsteCu || alterInTagen(juengsteCu.datum) > MAX_ALTER_TAGE;

  return (
    <div>
      <h3 style={{ marginTop: 0 }}>Metallnotierungen</h3>
      <p style={{ color: 'var(--ink-2)', marginBottom: 16, fontSize: 14 }}>
        Tageskurse für den Kupfer-/Metallzuschlag auf Kabel und Leitungen (EUR je 100 kg,
        z.B. die Westmetall DEL-Notiz). Der Zuschlag wird beim Anlegen einer Position
        eingefroren — ein späterer Kurswechsel ändert bestehende Angebote nicht.
        Pro Metall und Tag ist genau ein Kurs zulässig.
      </p>

      {cuVeraltet && !loading && (
        <div style={{
          padding: '12px 14px', marginBottom: 16, fontSize: 13,
          background: 'var(--bone-2)', border: '1px solid var(--bone-3)',
        }}>
          {juengsteCu
            ? `Die jüngste Kupfernotierung ist vom ${formatDatum(juengsteCu.datum)} ` +
              `(${alterInTagen(juengsteCu.datum)} Tage alt) — Kabelpositionen werden mit ` +
              'einem veralteten Kurs kalkuliert.'
            : 'Es ist noch keine Kupfernotierung gepflegt — für Metallartikel wird kein ' +
              'Zuschlag berechnet.'}
        </div>
      )}

      {fehler && (
        <div style={{
          padding: '12px 14px', marginBottom: 16, fontSize: 13, color: 'var(--red)',
          background: 'var(--bone-2)', border: '1px solid var(--bone-3)',
        }}>
          {fehler}
        </div>
      )}

      <form
        onSubmit={handleCreate}
        style={{ background: 'var(--bone-2)', padding: 16, marginBottom: 16 }}
      >
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1.5fr', gap: 12 }}>
          <select
            value={form.metall}
            onChange={e => setForm(f => ({ ...f, metall: e.target.value }))}
            style={{ padding: 8 }}
          >
            <option value="CU">Kupfer (CU)</option>
            <option value="AL">Aluminium (AL)</option>
          </select>
          <input
            type="date"
            required
            value={form.datum}
            onChange={e => setForm(f => ({ ...f, datum: e.target.value }))}
            style={{ padding: 8 }}
          />
          <input
            type="number"
            step="0.01"
            min="0.01"
            required
            placeholder="Kurs EUR / 100 kg *"
            value={form.kurs_eur_100kg}
            onChange={e => setForm(f => ({ ...f, kurs_eur_100kg: e.target.value }))}
            style={{ padding: 8 }}
          />
          <input
            placeholder="Quelle"
            value={form.quelle}
            onChange={e => setForm(f => ({ ...f, quelle: e.target.value }))}
            style={{ padding: 8 }}
          />
        </div>
        <button type="submit" className="k-btn k-btn--primary" disabled={saving} style={{ marginTop: 12 }}>
          {saving ? 'Speichert…' : 'Kurs erfassen'}
        </button>
      </form>

      {loading ? (
        <div style={{ padding: 16 }}>Lade Notierungen…</div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: 'var(--bone-3)' }}>
              <th style={{ padding: '8px 12px', textAlign: 'left' }}>Metall</th>
              <th style={{ padding: '8px 12px', textAlign: 'left' }}>Datum</th>
              <th style={{ padding: '8px 12px', textAlign: 'right' }}>EUR / 100 kg</th>
              <th style={{ padding: '8px 12px', textAlign: 'left' }}>Quelle</th>
              <th style={{ padding: '8px 12px', textAlign: 'right' }}>Aktion</th>
            </tr>
          </thead>
          <tbody>
            {notierungen.map(n => (
              <tr key={n.id} style={{ borderBottom: '1px solid var(--bone-3)' }}>
                <td style={{ padding: '8px 12px', fontWeight: 500 }}>{n.metall_bezeichnung}</td>
                <td style={{ padding: '8px 12px' }}>{formatDatum(n.datum)}</td>
                <td style={{ padding: '8px 12px', textAlign: 'right' }}>
                  {n.kurs_eur_100kg.toFixed(2)}
                </td>
                <td style={{ padding: '8px 12px', color: 'var(--ink-2)' }}>{n.quelle || '—'}</td>
                <td style={{ padding: '8px 12px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                  <button
                    type="button"
                    className="k-btn k-btn--ghost k-btn--sm"
                    onClick={() => handleKursKorrigieren(n)}
                  >
                    Korrigieren
                  </button>
                  <button
                    type="button"
                    className="k-btn k-btn--danger k-btn--sm"
                    onClick={() => handleLoeschen(n)}
                    style={{ marginLeft: 8 }}
                  >
                    Löschen
                  </button>
                </td>
              </tr>
            ))}
            {notierungen.length === 0 && (
              <tr>
                <td colSpan={5} style={{ padding: 24, textAlign: 'center', color: 'var(--ink-2)' }}>
                  Noch keine Notierungen erfasst.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default MetallSection;
